/* global twemoji */
import { Form, InputNumber, Select } from 'antd';
import { Suspense, useState } from 'react';
import {
  maxFontSize,
  minFontSize,
  selectorMaxWidth,
  selectorMinWidth,
} from '../../../constants/gridConfig';
import { useStore } from '../../../store/store';
import FontPicker from '../../common/FontPicker';
import { FamilyPicker } from '../../common/FamilyPicker';
import { ColorPaletteButton } from '../../common/ColorPaletteButton';
import { iconButtonStyle } from '../../../utils/styles';
import { bulletList } from '../../../utils/bulletList';
import './fontTab.css';
import { useTranslation } from 'react-i18next';
import emojiToolkit from 'emoji-toolkit';
import { useShareWithConfigPreview } from '../../../contexts/ConfigPreviewContext';

// Puce affichée par niveau quand le réglage vaut « Defaut » (cf. useBulletDraftJs).
const DEFAULT_BULLETS = ['•', '◦', '▪', '▪', '▪'];


const selector = ({
  configuration,
  setConfiguration,
}) => ({
  configuration,
  setConfiguration,
});

export const TabsFonts = () => {
  const { configuration, setConfiguration } = useStore(selector);
  const { Option } = Select;
  const [previewFamily, setPreviewFamily] = useState(configuration.fontFamily);
  // Police essayée montrée aussi par la page d'aperçu du Drawer.
  useShareWithConfigPreview('fontFamily', previewFamily);
  const { t } = useTranslation();
  const transparentColor =
    configuration.colors.find((color) => color.label === 'transparent') || {
      label: 'transparent',
      style: 'transparent',
    };
  // « Transparent » n'est pas une puce invisible : elle suit la couleur du texte
  // (cf. getBulletColor). La palette le dit, en tête, sous ce nom.
  const bulletColors = [
    {
      ...transparentColor,
      name: t('font-color'),
      inheritStyle: configuration.baseFontColor.style,
    },
    ...configuration.colors.filter((color) => color.label !== 'transparent'),
  ];

  const updatePreview = (family) => {
    setPreviewFamily(family);
  }

  const handleSymbol = (element, number) => {
    if (element === 'Defaut') {
      return setConfiguration({
        ...configuration,
        unorderedListBulletValue0: '•',
        unorderedListBulletValue1: '◦',
        unorderedListBulletValue2: '▪',
        unorderedListBulletValue3: '▪',
        unorderedListBulletValue4: '▪',
        customBulletPadding: '0px 0px 0px 0px',
      });
    }
    element = `"${element}"`;
    setConfiguration({
      ...configuration,
      [`unorderedListBulletValue${number}`]: element,
      customBulletPadding: '0px 0px 0px 5px',
    });
  };

  const handleChangeBaseFontColor = (selectedColor) => {
    setConfiguration({
      ...configuration,
      baseFontColor: configuration.colors.find(
        (color) => color.label === selectedColor
      ),
    });
  };

  const handleChangeBaseFontFamily = (selectedFont) => {
    setConfiguration({
      ...configuration,
      fontFamily: selectedFont,
    });
    updatePreview(selectedFont)
  };

  const handleChangeDefaultFontSize = (selectedSize) => {
    const updatedTextStyles = configuration.textStyles.map((style) => (
      style.id === 0
        ? { ...style, fontSize: selectedSize }
        : style
    ));

    setConfiguration({
      ...configuration,
      fontSize: selectedSize,
      textStyles: updatedTextStyles,
    });
  };

  // Couleur réellement tracée pour la puce d'un niveau : la sienne, ou celle du
  // texte quand elle est transparente — `getBulletColor` la rend alors `inherit`.
  const bulletColorStyle = (levelIndex) => {
    const bulletColor = configuration[`unorderedListBulletColor${levelIndex}`];
    return !bulletColor || bulletColor.label === 'transparent'
      ? configuration.baseFontColor.style
      : bulletColor.style;
  };

  const handleChangeBulletColor = (selectedColor, levelIndex) => {
    setConfiguration({
      ...configuration,
      [`unorderedListBulletColor${levelIndex}`]:
        configuration.colors.find((color) => color.label === selectedColor) ||
        transparentColor,
    });
  };

  const getEmoji = (element) => {
    // « Defaut » est la valeur stockée : seul son libellé prend l'accent.
    if (element === 'Defaut') return 'Défaut';
    const escapedFind = emojiToolkit.escapeRegExp(
      emojiToolkit.unicodeCharRegex()
    );
    const elementToUse = element;
    const search = new RegExp(`(${escapedFind})`, 'gi');
    if (search.exec(elementToUse) === null) {
      return elementToUse?.length === 3 ? elementToUse : `${elementToUse}`;
    }
    const twemojiElem = twemoji.parse(elementToUse, {
      folder: 'svg',
      ext: '.svg',
      base: 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@17.0.3/assets/',
    });
    // Create a temporary DOM element to extract the URL
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = twemojiElem;

    // Find the image element and extract the `src` attribute
    const twemojiImg = tempDiv.querySelector('img'); // Find the first img element
    const emojiUrl = twemojiImg
      ? <img
        src={twemojiImg.src}
        alt=""
        style={{ width: "1em", height: "1em" }}
      />
      : elementToUse?.length === 3
        ? elementToUse
        : `${elementToUse}`;

    return emojiUrl;
  }

  return (
    <>
      <Form.Item
        name='fontSize'
        label={'Taille de police par défaut'}
        initialValue={configuration.fontSize}
      >
        <InputNumber min={minFontSize} max={maxFontSize} onChange={handleChangeDefaultFontSize} />
      </Form.Item>
      {/* Bouton à palette de la barre de style, comme le numéro de page : la
          couleur choisie se lit sous le « A ». Pas de transparent — un texte
          par défaut invisible ne se remarque qu'une fois le PDF sorti. */}
      <Form.Item label='Couleur de police par défaut'>
        <ColorPaletteButton
          title='Couleur de police par défaut'
          disableTransparent
          value={configuration.baseFontColor}
          colors={configuration.colors}
          onChange={handleChangeBaseFontColor}
        />
      </Form.Item>
      <Form.Item
        name='fontStyle'
        label={'Style de police par défaut'}
      >
        <FontPicker onChange={handleChangeBaseFontFamily} forcedValue={configuration.fontFamily} />
      </Form.Item>
      <div style={{ display: "flex", flexDirection: "row", alignItems: "left", gap: 16, width: "100%" }}>
        <Suspense fallback='Chargement de Google font'>
          <Form.Item
            name='fontFamily'
            label={t('available-fonts')}
          >
            <FamilyPicker selectFamily={updatePreview} />
          </Form.Item>
        </Suspense>
      </div >
      <Form.Item
        colon={true}
        label={t('lists-symbols')}>
      </Form.Item>
      {
        [1, 2, 3, 4, 5].map((level, index) => {
          return (
            <Form.Item
              key={level}
              label={`Niveau ${level}`}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Select
                  value={configuration[`unorderedListBulletValue${index}`].replace(/['"]+/g, '')}
                  onSelect={(symbol) => {
                    handleSymbol(symbol, index);
                  }}
                  style={{
                    maxWidth: selectorMaxWidth,
                    minWidth: selectorMinWidth,
                    textAlign: 'center',
                  }}
                >
                  {bulletList.map((item) => {
                    return (
                      <Option
                        key={item}
                        value={item}
                        style={{
                          textAlign: 'center',
                          fontSize: '1.3vw',
                          fontFamily: 'Free Serif'
                        }}
                      >
                        {getEmoji(item)}
                      </Option>
                    );
                  })}
                </Select>
                {/* La puce du bouton est tracée de la couleur qu'elle aura dans
                    le document : le symbole choisi, dans sa couleur. Transparent
                    reste offert — il ne masque pas la puce, il la fait suivre la
                    couleur du texte (cf. getBulletColor), et c'est donc cette
                    couleur-là qui s'affiche ici. */}
                <ColorPaletteButton
                  title='Couleur de la puce'
                  icon={
                    <p style={{ ...iconButtonStyle(), color: bulletColorStyle(index) }}>
                      {/* « Defaut » s'affichait en toutes lettres, tronqué en
                          « efau » : on montre la puce par défaut du niveau
                          (cf. useBulletDraftJs). */}
                      {configuration[`unorderedListBulletValue${index}`] === 'Defaut'
                        ? DEFAULT_BULLETS[index]
                        : configuration[`unorderedListBulletValue${index}`].replace(/['"]+/g, '')}
                    </p>
                  }
                  value={configuration[`unorderedListBulletColor${index}`] ?? transparentColor}
                  colors={bulletColors}
                  onChange={(selectedColor) => handleChangeBulletColor(selectedColor, index)}
                />
              </div>
            </Form.Item >
          );
        })
      }
    </>
  );
};
